import React, { useState, useEffect, useCallback } from 'react';
import {
  DndContext, PointerSensor, TouchSensor, useSensor, useSensors,
  useDraggable, useDroppable, DragOverlay, closestCenter,
} from '@dnd-kit/core';
import {
  Plus, Sparkle, PaperPlaneRight, Trash, X, CookingPot, ArrowsClockwise,
  PencilSimple, CheckCircle, Check, DotsSixVertical,
  DownloadSimple, BookOpen, ListChecks,
} from '@phosphor-icons/react';
import { Modal, InputField } from './Shared';
import { slotToChips, dishKeyOf as defaultDishKeyOf } from './utils';
import { MEAL_SLOTS } from './constants';
import { apiOrigin } from '../../utils/apiBase';
import RatingModal from './RatingModal';
import ExportMenuModal from './ExportMenuModal';
import ImportPlansModal from './ImportPlansModal';
import MenuBoardView from './MenuBoardView';
import ShoppingListModal from './ShoppingListModal';
import DishDetailDrawer from './DishDetailDrawer';
import SwapChipPopover from './SwapChipPopover';
import ChipFeedbackMenu from './ChipFeedbackMenu';
import WeeklyRatingPass from './WeeklyRatingPass';
import MoveDayPopover from './MoveDayPopover';
import ServingsBadge, { StaffMenuStrip } from './ServingsBadge';
import BriefCard from './BriefCard';

// Resolve a relative /api/... image URL into an absolute one the browser can fetch.
const absUrl = (u) => (u && u.startsWith('/') ? `${apiOrigin()}${u}` : u);

function DraggableChip({ planId, slotKey, index, value, isMultiEater, onEdit, onRemove, onRate, onRecipe, onSwap, showImages, imageSrc, imageLoading, onGenerateImage, cookProgress, api, onFeedbackDone }) {
  // In multi-eater mode chip value looks like "Kalpesh: Poha" — feedback should
  // still be possible on the actual dish name (the part after the ":").
  const parsedDishName = (() => {
    const s = String(value ?? '');
    if (!isMultiEater) return s;
    const colonAt = s.indexOf(':');
    return colonAt >= 0 ? s.slice(colonAt + 1).trim() : s;
  })();
  const dragData = { type: 'chip', plan_id: planId, slot: slotKey, index, value };
  const { setNodeRef: setDragRef, attributes, listeners, isDragging } = useDraggable({
    id: `chip-${planId}-${slotKey}-${index}`,
    data: dragData,
    disabled: isMultiEater,
  });
  const { setNodeRef: setDropRef, isOver } = useDroppable({
    id: `chip-drop-${planId}-${slotKey}-${index}`,
    data: { type: 'chip', plan_id: planId, slot: slotKey, index },
    disabled: isMultiEater,
  });
  const setRef = (node) => { setDragRef(node); setDropRef(node); };
  // Tracks the actual <img> load state separately from cache-lookup state. While
  // the binary is still downloading we keep the spinner visible UNDER the img so
  // the user doesn't see an empty box.
  const [imgLoaded, setImgLoaded] = React.useState(false);
  React.useEffect(() => { setImgLoaded(false); }, [imageSrc]);

  if (showImages) {
    const showSpinner = imageLoading || (imageSrc && !imgLoaded);
    return (
      <div ref={setRef}
        className={`group relative w-28 rounded-xl overflow-hidden select-none ${isDragging ? 'opacity-30' : ''} ${isOver ? 'ring-1 ring-[#FF6B35]/60' : ''}`}
        style={{ background: 'rgba(255,255,255,0.05)', border: '1px solid rgba(255,255,255,0.10)' }}
        data-testid={`plan-chip-${slotKey}-${index}`}>
        <div className="relative w-full h-20 cursor-pointer"
          onClick={() => imageSrc && onRecipe && onRecipe()}
          title={imageSrc && onRecipe ? 'Tap for details, varieties, recipe & video' : undefined}
          style={{ background: imageSrc || imageLoading ? 'rgba(168,85,247,0.04)' : 'rgba(168,85,247,0.06)' }}>
          {imageSrc && (
            <img src={imageSrc} alt={value} loading="lazy" decoding="async"
              onLoad={() => setImgLoaded(true)}
              onError={() => setImgLoaded(true)}
              data-testid={`chip-image-${slotKey}-${index}`}
              className={`w-full h-20 object-cover transition-opacity duration-300 ${imgLoaded ? 'opacity-100' : 'opacity-0'}`} />
          )}
          {imageSrc && imgLoaded && onRecipe && (
            <div className="absolute inset-0 bg-black/0 hover:bg-black/40 transition-colors flex items-end justify-center pb-1.5 opacity-0 hover:opacity-100 pointer-events-none">
              <span className="text-[9px] font-bold text-white tracking-wide drop-shadow-md">Tap for details</span>
            </div>
          )}
          {showSpinner && (
            <div className="absolute inset-0 flex items-center justify-center pointer-events-none"
              data-testid={`chip-image-loading-${slotKey}-${index}`}>
              <span className="inline-block w-4 h-4 rounded-full border-2 border-purple-400/40 border-t-purple-300 animate-spin" />
            </div>
          )}
          {!imageSrc && !imageLoading && (
            <div className="absolute inset-0 flex items-center justify-center">
              <button onClick={() => onGenerateImage && onGenerateImage(value)}
                data-testid={`chip-image-gen-${slotKey}-${index}`}
                className="text-[9px] font-bold text-purple-300 hover:text-purple-200 flex items-center gap-1 px-1.5 py-1 rounded"
                title="Generate image">
                <Sparkle size={10} weight="fill" /> Generate
              </button>
            </div>
          )}
        </div>
        <div className="p-1.5 flex items-start gap-1">
          {!isMultiEater && (
            <span {...attributes} {...listeners}
              className="cursor-grab active:cursor-grabbing text-neutral-500 hover:text-neutral-300 mt-0.5"
              data-testid={`plan-chip-drag-${slotKey}-${index}`}>
              <DotsSixVertical size={9} weight="bold" />
            </span>
          )}
          <span onClick={onRate} className="text-[10px] text-neutral-200 cursor-pointer hover:text-yellow-300 leading-tight flex-1 line-clamp-2">{value}</span>
          {!isMultiEater ? (
            <div className="flex flex-col gap-0.5">
              {onSwap && (
                <button onClick={onSwap} data-testid={`plan-chip-swap-${slotKey}-${index}`}
                  className="text-neutral-400 hover:text-amber-300 transition-colors" title="AI: swap this dish">
                  <ArrowsClockwise size={9} weight="bold" />
                </button>
              )}
              {onRecipe && (
                <button onClick={onRecipe} data-testid={`plan-chip-recipe-${slotKey}-${index}`}
                  className="text-neutral-400 hover:text-emerald-300 transition-colors" title="View recipe">
                  <BookOpen size={9} weight="bold" />
                </button>
              )}
              {api && (
                <ChipFeedbackMenu api={api} dishName={parsedDishName} planId={planId} slot={slotKey}
                  onDone={onFeedbackDone}
                  testIdPrefix={`plan-chip-${slotKey}-${index}`} />
              )}
              <button onClick={onRemove} data-testid={`plan-chip-remove-${slotKey}-${index}`}
                className="text-neutral-400 hover:text-red-300 opacity-0 group-hover:opacity-100 transition-opacity" title="Remove">
                <X size={9} weight="bold" />
              </button>
            </div>
          ) : (
            // Multi-eater: only the 3-dot feedback menu (no drag/swap/remove — that's slot-level)
            api && (
              <ChipFeedbackMenu api={api} dishName={parsedDishName} planId={planId} slot={slotKey}
                onDone={onFeedbackDone}
                testIdPrefix={`plan-chip-${slotKey}-${index}`} />
            )
          )}
        </div>
        {cookProgress && cookProgress.total_steps > 0 && (
          <CookProgressBar progress={cookProgress} testId={`plan-chip-progress-${slotKey}-${index}`} />
        )}
      </div>
    );
  }

  return (
    <div ref={setRef}
      className={`group inline-flex flex-col gap-1 px-2 py-1 rounded-lg text-[11px] font-medium select-none ${isDragging ? 'opacity-30' : ''} ${isOver ? 'ring-1 ring-[#FF6B35]/60' : ''}`}
      style={{ background: 'rgba(255,255,255,0.05)', border: '1px solid rgba(255,255,255,0.10)' }}
      data-testid={`plan-chip-${slotKey}-${index}`}>
      <div className="inline-flex items-center gap-1">
      {!isMultiEater && (
        <span {...attributes} {...listeners}
          className="cursor-grab active:cursor-grabbing text-neutral-500 hover:text-neutral-300"
          data-testid={`plan-chip-drag-${slotKey}-${index}`}
          title="Drag to reorder or move">
          <DotsSixVertical size={10} weight="bold" />
        </span>
      )}
      <span onClick={onRate} className="text-neutral-200 cursor-pointer hover:text-yellow-300" title="Rate this dish">{value}</span>
      {!isMultiEater ? (
        <>
          {onSwap && (
            <button onClick={onSwap} data-testid={`plan-chip-swap-${slotKey}-${index}`}
              className="text-neutral-400 hover:text-amber-300 transition-colors" title="AI: swap this dish">
              <ArrowsClockwise size={10} weight="bold" />
            </button>
          )}
          {onRecipe && (
            <button onClick={onRecipe} data-testid={`plan-chip-recipe-${slotKey}-${index}`}
              className="text-neutral-400 hover:text-emerald-300 transition-colors" title="View recipe">
              <BookOpen size={10} weight="bold" />
            </button>
          )}
          {api && (
            <ChipFeedbackMenu api={api} dishName={parsedDishName} planId={planId} slot={slotKey}
              onDone={onFeedbackDone}
              testIdPrefix={`plan-chip-${slotKey}-${index}`} />
          )}
          <button onClick={onEdit} data-testid={`plan-chip-edit-${slotKey}-${index}`}
            className="opacity-0 group-hover:opacity-100 text-neutral-400 hover:text-white transition-opacity" title="Edit">
            <PencilSimple size={10} />
          </button>
          <button onClick={onRemove} data-testid={`plan-chip-remove-${slotKey}-${index}`}
            className="text-neutral-400 hover:text-red-300" title="Remove">
            <X size={10} weight="bold" />
          </button>
        </>
      ) : (
        api && (
          <ChipFeedbackMenu api={api} dishName={parsedDishName} planId={planId} slot={slotKey}
            onDone={onFeedbackDone}
            testIdPrefix={`plan-chip-${slotKey}-${index}`} />
        )
      )}
      </div>
      {cookProgress && cookProgress.total_steps > 0 && (
        <CookProgressBar progress={cookProgress} testId={`plan-chip-progress-${slotKey}-${index}`} />
      )}
    </div>
  );
}

// Tiny progress bar + label rendered under any chip with cook progress.
// Two states:
//  • while cooking → "Ramu cooking · 22 min" (live, re-renders every 60s)
//  • after done    → "8:15 → 8:53 · 38 min" (absolute times + total duration)
function CookProgressBar({ progress, testId }) {
  const [, setTick] = useState(0);
  // Re-render once a minute while the dish is still in progress so the
  // "22 min" label keeps ticking up. Stops once `completed_at` is set.
  useEffect(() => {
    if (progress?.completed_at) return;
    const id = setInterval(() => setTick(t => t + 1), 60_000);
    return () => clearInterval(id);
  }, [progress?.completed_at]);

  const done = (progress.completed_steps || []).length;
  const total = progress.total_steps || 0;
  if (total <= 0) return null;
  const pct = Math.min(100, Math.round((done / total) * 100));
  const isDone = !!progress.completed_at || done >= total;
  const cook = (progress.cook_name || '').trim();

  // Format helpers — local time for absolute, minutes for durations.
  const fmtTime = (iso) => {
    try {
      const d = new Date(iso);
      return d.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit', hour12: false });
    } catch { return ''; }
  };
  const minsBetween = (a, b) => {
    try {
      const da = new Date(a).getTime(), db = new Date(b).getTime();
      return Math.max(0, Math.round((db - da) / 60_000));
    } catch { return 0; }
  };

  let stateLabel = '';
  if (isDone && progress.started_at && progress.completed_at) {
    const start = fmtTime(progress.started_at);
    const end = fmtTime(progress.completed_at);
    const dur = minsBetween(progress.started_at, progress.completed_at);
    stateLabel = `${start} → ${end} · ${dur} min`;
  } else if (!isDone && progress.started_at) {
    const dur = minsBetween(progress.started_at, new Date().toISOString());
    stateLabel = `cooking · ${dur} min`;
  }

  return (
    <div className="w-full" data-testid={testId}>
      <div className="h-[3px] w-full rounded-full bg-white/10 overflow-hidden">
        <div className={`h-full rounded-full transition-all duration-500 ${isDone ? 'bg-emerald-400' : 'bg-[#FF6B35]'}`}
          style={{ width: `${pct}%` }} />
      </div>
      <p className={`text-[9px] mt-0.5 font-semibold leading-tight ${isDone ? 'text-emerald-400' : 'text-[#FF6B35]'}`}>
        {isDone ? '✓ Done' : `${done}/${total}`}{cook ? ` · ${cook}` : ''}
        {stateLabel && <span className="block opacity-80 font-normal">{stateLabel}</span>}
      </p>
    </div>
  );
}

function PlanSlotEditor({ planId, slotKey, slotLabel, value, onChange, onRegenerate, onRateDish, onRecipeOpen, onSwapChip, showImages, dishImages, dishKeyOf, onGenerateImage, imagesFetched, inFlightKeys, paintJobKeys, cookProgress, api, onFeedbackDone }) {
  const chips = slotToChips(value);
  const [adding, setAdding] = useState(false);
  const [draft, setDraft] = useState('');
  const [editIdx, setEditIdx] = useState(-1);
  const [editVal, setEditVal] = useState('');
  const isMultiEater = value && typeof value === 'object' && !Array.isArray(value);

  const { setNodeRef: setDropRef, isOver } = useDroppable({
    id: `drop-${planId}-${slotKey}`,
    data: { type: 'slot', plan_id: planId, slot: slotKey },
    disabled: isMultiEater,
  });

  const removeChip = (i) => {
    const next = chips.filter((_, idx) => idx !== i);
    onChange(next);
  };
  const addChip = () => {
    const v = (draft || '').trim();
    if (!v) { setAdding(false); return; }
    onChange([...chips, v]);
    setDraft('');
    setAdding(false);
  };
  const saveEdit = () => {
    const v = (editVal || '').trim();
    if (!v) { setEditIdx(-1); return; }
    const next = chips.map((c, idx) => idx === editIdx ? v : c);
    onChange(next);
    setEditIdx(-1); setEditVal('');
  };

  if (chips.length === 0 && !adding) {
    return (
      <div ref={setDropRef}
        className={`flex items-center justify-between gap-2 rounded-lg transition-colors ${isOver ? 'bg-[#FF6B35]/10 ring-1 ring-[#FF6B35]/40 px-2 py-1' : ''}`}
        data-testid={`plan-slot-${slotKey}`}>
        <span className="text-[9px] uppercase tracking-wider text-neutral-500">{slotLabel}</span>
        <div className="flex gap-1">
          <button onClick={() => setAdding(true)} data-testid={`plan-slot-add-${slotKey}`}
            className="p-1 rounded-lg bg-white/5 hover:bg-white/10 text-neutral-400" title="Add dish">
            <Plus size={11} />
          </button>
          <button onClick={onRegenerate} data-testid={`plan-slot-regen-${slotKey}`}
            className="p-1 rounded-lg bg-[#FF6B35]/10 text-[#FF6B35] hover:bg-[#FF6B35]/20" title="Re-roll with AI">
            <ArrowsClockwise size={11} weight="bold" />
          </button>
        </div>
      </div>
    );
  }

  return (
    <div data-testid={`plan-slot-${slotKey}`}>
      <div className="flex items-center justify-between gap-2 mb-1.5">
        <span className="text-[9px] uppercase tracking-wider text-neutral-500">{slotLabel}</span>
        <div className="flex gap-1">
          {!isMultiEater && (
            <button onClick={() => setAdding(true)} data-testid={`plan-slot-add-${slotKey}`}
              className="p-1 rounded-lg bg-white/5 hover:bg-white/10 text-neutral-400" title="Add dish">
              <Plus size={11} />
            </button>
          )}
          <button onClick={onRegenerate} data-testid={`plan-slot-regen-${slotKey}`}
            className="p-1 rounded-lg bg-[#FF6B35]/10 text-[#FF6B35] hover:bg-[#FF6B35]/20" title="Re-roll with AI">
            <ArrowsClockwise size={11} weight="bold" />
          </button>
        </div>
      </div>
      <div ref={setDropRef}
        className={`flex flex-wrap gap-1.5 rounded-lg p-1 transition-colors ${isOver ? 'bg-[#FF6B35]/10 ring-1 ring-[#FF6B35]/40' : ''}`}
        data-testid={`plan-slot-chips-${slotKey}`}>
        {chips.map((c, i) => (
          editIdx === i ? (
            <div key={`edit-${i}`} className="inline-flex items-center gap-1 px-2 py-1 rounded-full text-[11px] font-medium"
              style={{ background: 'rgba(255,255,255,0.05)', border: '1px solid rgba(255,107,53,0.40)' }}>
              <input value={editVal} onChange={e => setEditVal(e.target.value)} autoFocus
                onKeyDown={e => { if (e.key === 'Enter') saveEdit(); if (e.key === 'Escape') setEditIdx(-1); }}
                className="bg-transparent outline-none border-b border-purple-400 w-32 text-white" />
              <button onClick={saveEdit} className="text-emerald-300 hover:text-emerald-200"><Check size={10} weight="bold" /></button>
              <button onClick={() => setEditIdx(-1)} className="text-neutral-400 hover:text-white"><X size={10} weight="bold" /></button>
            </div>
          ) : (
            <DraggableChip
              key={`${planId}-${slotKey}-${i}-${c}`}
              planId={planId}
              slotKey={slotKey}
              index={i}
              value={c}
              isMultiEater={isMultiEater}
              onEdit={() => { setEditIdx(i); setEditVal(c); }}
              onRemove={() => removeChip(i)}
              onRate={() => onRateDish(c)}
              onRecipe={onRecipeOpen ? () => onRecipeOpen(c) : null}
              onSwap={onSwapChip ? () => onSwapChip(c, i, slotKey) : null}
              showImages={showImages}
              imageSrc={showImages && dishImages ? dishImages[(dishKeyOf || (() => ''))(c)] : null}
              imageLoading={(() => {
                if (!showImages) return false;
                const k = (dishKeyOf || (() => ''))(c);
                if (!k) return false;
                if (dishImages && dishImages[k]) return false;            // already loaded
                if (inFlightKeys && inFlightKeys.has(k)) return true;     // single Generate in progress
                if (paintJobKeys && paintJobKeys.has(k)) return true;     // bulk paint queued/running
                if (!imagesFetched) return true;                          // initial cache lookup pending
                return false;
              })()}
              onGenerateImage={onGenerateImage}
              cookProgress={cookProgress && cookProgress[`${planId}|${slotKey}|${(dishKeyOf || (() => ''))(c)}`]}
              api={api}
              onFeedbackDone={onFeedbackDone}
            />
          )
        ))}
        {adding && (
          <div className="inline-flex items-center gap-1 px-2 py-1 rounded-full"
            style={{ background: 'rgba(255,107,53,0.10)', border: '1px solid rgba(255,107,53,0.30)' }}>
            <input value={draft} onChange={e => setDraft(e.target.value)} autoFocus
              onKeyDown={e => { if (e.key === 'Enter') addChip(); if (e.key === 'Escape') { setDraft(''); setAdding(false); } }}
              placeholder="Add dish…" className="bg-transparent outline-none w-28 text-[11px] text-white" data-testid={`plan-chip-input-${slotKey}`} />
            <button onClick={addChip} className="text-emerald-300 hover:text-emerald-200"><Check size={10} weight="bold" /></button>
            <button onClick={() => { setDraft(''); setAdding(false); }} className="text-neutral-400 hover:text-white"><X size={10} weight="bold" /></button>
          </div>
        )}
      </div>
    </div>
  );
}

function PaintEta({ job }) {
  // Live countdown: heuristic start (18s/image, 8 parallel), then adapts to the
  // real observed rate once images start completing. Ticks every second.
  const [, force] = React.useReducer(x => x + 1, 0);
  React.useEffect(() => {
    if (job.status !== 'running') return;
    const t = setInterval(force, 1000);
    return () => clearInterval(t);
  }, [job.status]);
  if (job.status !== 'running' || !job.startedAt || !job.total) return null;
  const completed = (job.done || 0) + (job.failed || 0);
  const elapsed = Math.max(1, (Date.now() - job.startedAt) / 1000);
  let remaining;
  if (completed > 0) remaining = ((job.total - completed) * elapsed) / completed;
  else remaining = Math.max(5, Math.ceil(job.total / 8) * 18 - elapsed);
  remaining = Math.max(0, Math.round(remaining));
  const m = Math.floor(remaining / 60), s = remaining % 60;
  return (
    <span className="text-[10px] font-bold text-purple-300/90 tabular-nums flex-shrink-0" data-testid="paint-eta">
      {m > 0 ? `~${m}m ${String(s).padStart(2, '0')}s left` : `~${s}s left`}
    </span>
  );
}

function PlanDayCard({ plan, selected, onToggleSelect, onNotifyCook, onSlotChange, onRegenerateSlot, onClearDay, onDeleteDay, onRateDish, onRecipeOpen, onSwapChip, onSetServings, hasStaffMeals, staffSharingMode, api, onUpdate, showImages, dishImages, dishKeyOf, onGenerateImage, imagesFetched, inFlightKeys, paintJobKeys, cookProgress, jainFlag }) {
  const p = plan;
  return (
    <div data-testid={`plan-${p.plan_id}`}
      className={`rounded-2xl p-4 ${selected ? 'ring-1 ring-purple-500/50' : ''}`}
      style={{ background: selected ? 'rgba(168,85,247,0.06)' : 'rgba(255,255,255,0.03)', border: '1px solid rgba(255,255,255,0.06)', contentVisibility: 'auto', containIntrinsicSize: 'auto 420px' }}>
      <div className="flex items-center justify-between mb-3 gap-2">
        <label className="flex items-center gap-2 cursor-pointer min-w-0">
          <input type="checkbox" checked={selected} onChange={onToggleSelect}
            data-testid={`planner-select-${p.plan_id}`} className="accent-purple-500" />
          <span className="text-sm font-semibold truncate">
            {new Date(p.date + 'T00:00').toLocaleDateString('en-IN', { weekday: 'short', day: 'numeric', month: 'short' })}
          </span>
          {jainFlag?.paryushan && (
            <span data-testid={`paryushan-badge-${p.plan_id}`} title="Paryushan — strictly no vegetables"
              className="px-1.5 py-0.5 rounded-full text-[9px] font-bold uppercase tracking-wide flex-shrink-0 bg-rose-500/20 text-rose-300 border border-rose-500/40">
              🙏 Paryushan
            </span>
          )}
          {jainFlag?.label && !jainFlag?.paryushan && (
            <span data-testid={`jain-badge-${p.plan_id}`} title={jainFlag.followed ? 'No-vegetable day' : 'Tithi (not followed for meals)'}
              className={`px-1.5 py-0.5 rounded-full text-[9px] font-bold uppercase tracking-wide flex-shrink-0 ${jainFlag.followed ? 'bg-amber-500/20 text-amber-300 border border-amber-500/40' : 'bg-white/5 text-neutral-500 border border-white/10'}`}>
              🙏 {jainFlag.label}
            </span>
          )}
          {jainFlag?.chaturmas && !jainFlag?.label && !jainFlag?.paryushan && (
            <span data-testid={`chaturmas-badge-${p.plan_id}`} title="Chaturmas — no root vegetables"
              className="px-1.5 py-0.5 rounded-full text-[9px] font-bold uppercase tracking-wide flex-shrink-0 bg-emerald-500/15 text-emerald-300 border border-emerald-500/30">
              🙏 Chaturmas
            </span>
          )}
        </label>
        <ServingsBadge plan={p} onUpdate={onSetServings} />
        <div className="flex gap-1 flex-wrap justify-end">
          <MoveDayPopover api={api} plan={p} onDone={onUpdate} />
          <button onClick={() => onNotifyCook(p.plan_id, false)} data-testid={`notify-cook-${p.plan_id}`}
            className="px-2 py-1 text-[10px] rounded-lg bg-[#FF6B35]/10 text-[#FF6B35] hover:bg-[#FF6B35]/20 flex items-center gap-1">
            <PaperPlaneRight size={10} /> Cook
          </button>
          <button onClick={() => onNotifyCook(p.plan_id, true)} data-testid={`notify-cook-hindi-${p.plan_id}`}
            className="px-2 py-1 text-[10px] rounded-lg bg-blue-500/10 text-blue-400 hover:bg-blue-500/20 flex items-center gap-1">
            <PaperPlaneRight size={10} /> Hindi
          </button>
          <button onClick={() => onClearDay(p.plan_id)} data-testid={`planner-clear-${p.plan_id}`}
            className="px-2 py-1 text-[10px] rounded-lg bg-amber-500/10 text-amber-300 hover:bg-amber-500/20" title="Clear dishes for this day">
            Clear
          </button>
          <button onClick={() => onDeleteDay(p.plan_id)} data-testid={`planner-delete-${p.plan_id}`}
            className="p-1 rounded-lg bg-red-500/10 text-red-300 hover:bg-red-500/20" title="Delete this day's plan">
            <Trash size={11} />
          </button>
        </div>
      </div>
      <div className="space-y-3">
        {MEAL_SLOTS.map(({ key: mt, label }) => (
          <PlanSlotEditor
            key={mt}
            planId={p.plan_id}
            slotKey={mt}
            slotLabel={label}
            value={p[mt]}
            onChange={(items) => onSlotChange(p.plan_id, mt, items)}
            onRegenerate={() => onRegenerateSlot(p.plan_id, mt)}
            onRateDish={(dish) => onRateDish(dish, mt)}
            onRecipeOpen={(dish) => onRecipeOpen({ name: dish, plan_id: p.plan_id, slot: mt, date: p.date })}
            onSwapChip={onSwapChip}
            showImages={showImages}
            dishImages={dishImages}
            dishKeyOf={dishKeyOf}
            onGenerateImage={onGenerateImage}
            imagesFetched={imagesFetched}
            inFlightKeys={inFlightKeys}
            paintJobKeys={paintJobKeys}
            cookProgress={cookProgress}
            api={api}
            onFeedbackDone={onUpdate}
          />
        ))}
      </div>
      {p.sent_to_cook && <p className="text-[10px] text-green-400 mt-2 flex items-center gap-1"><CheckCircle size={10} /> Sent to cook</p>}
      {(hasStaffMeals || staffSharingMode === 'always_share') && (
        <StaffMenuStrip plan={p} api={api} onUpdate={onUpdate} />
      )}
    </div>
  );
}

export default function PlannerTab({ api, plans, prefs, onUpdate, onBriefAction }) {
  const [showPlanForm, setShowPlanForm] = useState(false);
  const [planForm, setPlanForm] = useState({ date: new Date().toISOString().slice(0, 10), breakfast: '', lunch: '', dinner: '', snacks: '', notes: '' });
  const [ratingModal, setRatingModal] = useState(null);
  const [selected, setSelected] = useState(new Set());
  const [showExport, setShowExport] = useState(false);
  const [showImport, setShowImport] = useState(false);
  const [boardMode, setBoardMode] = useState(false);
  const [showShopping, setShowShopping] = useState(false);
  const [recipePanelDish, setRecipePanelDish] = useState(null);
  const [swapState, setSwapState] = useState(null);

  // Live cook progress, keyed by `${plan_id}|${slot}|${dish_key}`. Polled
  // every 30s while the planner is mounted; refreshed instantly after a
  // share-with-cook so the chip starts at 0/N right after the cook opens
  // the link.
  const [cookProgress, setCookProgress] = useState({});
  const [jainFlags, setJainFlags] = useState({});

  useEffect(() => {
    const dates = [...new Set((plans || []).map(p => p.date))].slice(0, 60);
    if (!dates.length) { setJainFlags({}); return; }
    api.get(`/jain/flags?dates=${dates.join(',')}`).then(r => setJainFlags(r.data || {})).catch(() => {});
  }, [api, plans]);

  useEffect(() => {
    let cancelled = false;
    const fetchProgress = async () => {
      try {
        // Restrict to the date range visible in the planner (saves payload).
        const since = (plans || []).reduce((acc, p) => (p.date && (!acc || p.date < acc) ? p.date : acc), null);
        const r = await api.get('/meals/cook/progress/recent', { params: since ? { since } : {} });
        if (cancelled) return;
        const next = {};
        for (const it of (r.data?.items || [])) {
          if (!it.plan_id || !it.slot || !it.dish_key) continue;
          next[`${it.plan_id}|${it.slot}|${it.dish_key}`] = it;
        }
        setCookProgress(next);
      } catch (_) { /* polling is best-effort */ }
    };
    fetchProgress();
    const id = setInterval(fetchProgress, 30000);
    return () => { cancelled = true; clearInterval(id); };
  }, [api, plans]);

  const handleSavePlan = async (e) => {
    e.preventDefault();
    await api.post('/meals/plans', planForm);
    setShowPlanForm(false);
    setPlanForm({ date: new Date().toISOString().slice(0, 10), breakfast: '', lunch: '', dinner: '', snacks: '', notes: '' });
    onUpdate();
  };

  const handleNotifyCook = async (planId, hindi) => {
    try {
      if (hindi) {
        await api.post(`/meals/plans/${planId}/notify-cook-hindi`);
      } else {
        await api.post(`/meals/plans/${planId}/notify-cook`);
      }
      onUpdate();
    } catch (err) { console.error(err); }
  };

  const handleSlotChange = async (planId, slot, items) => {
    try {
      await api.patch(`/meals/plans/${planId}/slot`, { slot, items });
      onUpdate();
    } catch (e) { console.error(e); }
  };

  const handleRegenerateSlot = async (planId, slot) => {
    try {
      await api.post(`/meals/plans/${planId}/regenerate-slot`, { slot });
      onUpdate();
    } catch (e) {
      window.alert(e.response?.data?.detail || 'Regenerate failed. Check OpenAI key in Settings.');
    }
  };

  const handleClearDay = async (planId) => {
    if (!window.confirm('Clear all dishes for this day? Notes are kept.')) return;
    try {
      await api.post('/meals/plans/bulk', { action: 'clear', plan_ids: [planId] });
      onUpdate();
    } catch (e) { console.error(e); }
  };

  const handleDeleteDay = async (planId) => {
    if (!window.confirm('Delete this day\u2019s plan entirely?')) return;
    try {
      await api.delete(`/meals/plans/${planId}`);
      onUpdate();
    } catch (e) { console.error(e); }
  };

  const toggleSelect = (id) => {
    setSelected(prev => {
      const n = new Set(prev);
      if (n.has(id)) n.delete(id); else n.add(id);
      return n;
    });
  };
  const visibleIds = plans.filter(p => p.plan_id).map(p => p.plan_id);
  const allSelected = visibleIds.length > 0 && visibleIds.every(id => selected.has(id));
  const toggleSelectAll = () => {
    if (allSelected) setSelected(new Set());
    else setSelected(new Set(visibleIds));
  };
  const clearSelection = () => setSelected(new Set());
  const runBulk = async (action) => {
    const ids = Array.from(selected);
    if (ids.length === 0) return;
    const verb = action === 'delete' ? 'Delete' : 'Clear all dishes for';
    if (!window.confirm(`${verb} ${ids.length} day(s)?`)) return;
    try {
      await api.post('/meals/plans/bulk', { action, plan_ids: ids });
      setSelected(new Set());
      onUpdate();
    } catch (e) { console.error(e); }
  };

  const sensors = useSensors(
    useSensor(PointerSensor, { activationConstraint: { distance: 6 } }),
    useSensor(TouchSensor, { activationConstraint: { delay: 220, tolerance: 8 } }),
  );
  const [dragChip, setDragChip] = useState(null);
  // Default ON — images are the better default UX. Toggle persists per-session via state.
  const [showImages, setShowImages] = useState(true);
  // Phase 86 — Archive: past plans are collapsed into an expander at the bottom.
  const [showArchive, setShowArchive] = useState(false);
  const [dishImages, setDishImages] = useState({});
  const [paintJob, setPaintJob] = useState(null);
  // True once the initial /meals/dish-images?names=... GET has returned. While
  // false (and showImages is on), each chip shows a spinner instead of the
  // "Generate" button so the user knows we're checking the cache.
  const [imagesFetched, setImagesFetched] = useState(false);
  // Chips being individually generated (POST /dish-images) — keyed by dish_key.
  const [inFlightKeys, setInFlightKeys] = useState(new Set());
  // Tracks the chip-name set we've already auto-painted for, so we don't loop.
  const autoPaintedRef = React.useRef(new Set());
  // Cap auto-painting at this many dishes — beyond this we ask the user to opt in
  // via the Paint All button, to avoid surprise Gemini credit usage.
  const AUTO_PAINT_THRESHOLD = 100;

  const allChipNames = React.useMemo(() => {
    const names = new Set();
    plans.forEach(p => {
      ['breakfast', 'lunch', 'dinner', 'snacks'].forEach(slot => {
        slotToChips(p[slot]).forEach(c => { if (c) names.add(c); });
      });
    });
    return Array.from(names);
  }, [plans]);

  // Phase 86 — Split plans into upcoming (today onwards, chronological)
  // and past (yesterday and older, most-recent-first, collapsed by default).
  const { upcomingPlans, pastPlans } = React.useMemo(() => {
    const today = new Date();
    today.setHours(0, 0, 0, 0);
    const todayStr = today.toISOString().slice(0, 10);
    const up = [];
    const pa = [];
    (plans || []).forEach(p => {
      if (!p.date) { up.push(p); return; }
      if (p.date >= todayStr) up.push(p); else pa.push(p);
    });
    up.sort((a, b) => (a.date || '').localeCompare(b.date || ''));
    pa.sort((a, b) => (b.date || '').localeCompare(a.date || ''));
    return { upcomingPlans: up, pastPlans: pa };
  }, [plans]);

  useEffect(() => {
    if (!showImages || allChipNames.length === 0) {
      setImagesFetched(true);
      return;
    }
    let cancelled = false;
    setImagesFetched(false);
    (async () => {
      try {
        const r = await api.post('/meals/dish-images/lookup', { names: allChipNames });
        if (cancelled) return;
        const map = {};
        Object.entries(r.data || {}).forEach(([key, val]) => {
          if (val.image_url) map[key] = absUrl(val.image_url);
        });
        setDishImages(map);
      } catch (_) { /* ignore */ }
      finally { if (!cancelled) setImagesFetched(true); }
    })();
    return () => { cancelled = true; };
  }, [api, showImages, allChipNames]);

  const generateImageFor = async (name) => {
    const key = defaultDishKeyOf(name);
    setInFlightKeys(prev => { const next = new Set(prev); next.add(key); return next; });
    try {
      const r = await api.post('/meals/dish-images', { dish_name: name });
      if (r.data?.image_url) {
        setDishImages(prev => ({ ...prev, [r.data.dish_key]: absUrl(r.data.image_url) }));
      }
    } catch (e) {
      window.alert(e.response?.data?.detail || 'Image generation failed');
    } finally {
      setInFlightKeys(prev => { const next = new Set(prev); next.delete(key); return next; });
    }
  };

  const missingChipNames = React.useMemo(() => {
    if (!showImages) return [];
    return allChipNames.filter(n => !dishImages[defaultDishKeyOf(n)]);
  }, [showImages, allChipNames, dishImages]);

  // Set of dish_keys still being painted by the active bulk job (so chips can
  // show a spinner while the LLM is working).
  const paintJobKeys = React.useMemo(() => {
    if (!paintJob || paintJob.status !== 'running') return new Set();
    // missingChipNames is computed from the *latest* dishImages map, so it
    // shrinks naturally as the bulk poller refreshes cached images. Anything
    // still in there during a running job is "in flight".
    return new Set(missingChipNames.map(defaultDishKeyOf));
  }, [paintJob, missingChipNames]);

  const refreshCachedImages = useCallback(async () => {
    if (allChipNames.length === 0) return;
    try {
      const r = await api.post('/meals/dish-images/lookup', { names: allChipNames });
      const map = {};
      Object.entries(r.data || {}).forEach(([key, val]) => {
        if (val.image_url) map[key] = absUrl(val.image_url);
      });
      setDishImages(map);
    } catch (_) { /* ignore */ }
  }, [api, allChipNames]);

  const startPaintAll = async () => {
    if (missingChipNames.length === 0) return;
    if (missingChipNames.length > 50 && !window.confirm(`Generate ${missingChipNames.length} images? This may take ${Math.ceil(missingChipNames.length * 8 / 60)} minute(s) and uses Gemini credits.`)) return;
    try {
      const res = await api.post('/meals/dish-images/bulk', { dish_names: missingChipNames });
      setPaintJob({ job_id: res.data.job_id, total: res.data.queued, done: 0, failed: 0, status: 'running', startedAt: Date.now() });
    } catch (e) {
      window.alert(e.response?.data?.detail || 'Could not start bulk job');
    }
  };

  // Auto pre-warm: when This Week loads with cached fetch complete and there are
  // <= AUTO_PAINT_THRESHOLD missing thumbnails, silently kick off the bulk job so
  // chips paint in the background as the user scrolls. We dedupe via a ref keyed
  // on the joined missing-name set so the effect never loops.
  useEffect(() => {
    if (!showImages) return;
    if (paintJob && paintJob.status === 'running') return;
    if (missingChipNames.length === 0) return;
    if (missingChipNames.length > AUTO_PAINT_THRESHOLD) return;   // user must opt-in via button
    const key = missingChipNames.slice().sort().join('|');
    if (autoPaintedRef.current.has(key)) return;
    autoPaintedRef.current.add(key);
    (async () => {
      try {
        const res = await api.post('/meals/dish-images/bulk', { dish_names: missingChipNames });
        if (res.data?.queued > 0) {
          setPaintJob({ job_id: res.data.job_id, total: res.data.queued, done: 0, failed: 0, status: 'running', auto: true, startedAt: Date.now() });
        }
      } catch (_) { /* silent — user can still tap Paint All manually */ }
    })();
  }, [api, showImages, missingChipNames, paintJob]);

  useEffect(() => {
    if (!paintJob?.job_id) return;
    let cancelled = false;
    let lastDone = -1;
    let lastProgressAt = Date.now();
    let lastCount = -1;
    const tick = async () => {
      try {
        const r = await api.get(`/meals/dish-images/jobs/${paintJob.job_id}`);
        if (cancelled) return;
        const d = r.data || {};
        const count = (d.done || 0) + (d.failed || 0);
        if (count !== lastCount) { lastCount = count; lastProgressAt = Date.now(); }
        // No movement for 3 min → the server watchdog will revive it (runs every
        // 3 min); surface a "stalled" state with a manual retry as a fallback.
        const stalled = d.status !== 'complete' && Date.now() - lastProgressAt > 180000;
        const status = d.status === 'complete' ? 'complete' : (stalled ? 'stalled' : 'running');
        setPaintJob(prev => prev && prev.job_id === d.job_id ? { ...prev, done: d.done, failed: d.failed, total: d.total, status, error: d.error } : prev);
        if (d.done !== lastDone) {
          lastDone = d.done;
          refreshCachedImages();
        }
        if (status === 'complete') {
          refreshCachedImages();
          setTimeout(() => !cancelled && setPaintJob(null), 4000);
        }
      } catch (_) { /* ignore */ }
    };
    tick();
    const id = setInterval(tick, 3000);
    return () => { cancelled = true; clearInterval(id); };
  }, [api, paintJob?.job_id, refreshCachedImages]);

  const handleDragStart = (e) => {
    const d = e.active?.data?.current;
    if (d?.type === 'chip') setDragChip(d);
  };
  const handleDragEnd = async (e) => {
    setDragChip(null);
    const a = e.active?.data?.current;
    const o = e.over?.data?.current;
    if (!a || !o) return;
    if (a.type !== 'chip') return;
    let dst_plan_id, dst_slot, dst_index;
    if (o.type === 'chip') { dst_plan_id = o.plan_id; dst_slot = o.slot; dst_index = o.index; }
    else if (o.type === 'slot') { dst_plan_id = o.plan_id; dst_slot = o.slot; dst_index = -1; }
    else return;
    if (a.plan_id === dst_plan_id && a.slot === dst_slot && a.index === dst_index) return;
    try {
      await api.post('/meals/plans/move-chip', {
        src_plan_id: a.plan_id, src_slot: a.slot, src_index: a.index,
        dst_plan_id, dst_slot, dst_index,
      });
      onUpdate();
    } catch (err) {
      window.alert(err.response?.data?.detail || 'Move failed');
    }
  };

  return (
    <DndContext sensors={sensors} collisionDetection={closestCenter}
      onDragStart={handleDragStart} onDragEnd={handleDragEnd}>
    <div className="space-y-4">
      {onBriefAction && <BriefCard api={api} onActionTrigger={onBriefAction} />}
      <WeeklyRatingPass api={api} onDone={onUpdate} />
      <div className="flex items-center justify-between flex-wrap gap-2">
        <div className="flex items-center gap-3">
          <p className="text-sm text-neutral-400">{plans.length} meal plan(s)</p>
          {plans.length > 0 && (
            <label className="flex items-center gap-1.5 cursor-pointer" data-testid="planner-select-all">
              <input type="checkbox" checked={allSelected} onChange={toggleSelectAll} className="accent-purple-500" />
              <span className="text-[10px] uppercase tracking-wider font-bold text-neutral-500">
                {allSelected ? 'Deselect all' : 'Select all'}
              </span>
            </label>
          )}
          <span className="hidden sm:inline text-[10px] text-neutral-500 italic">tip: drag chips to reorder or move across slots/days</span>
        </div>
        <div className="flex items-center gap-2 flex-wrap">
          <button onClick={() => setShowImages(v => !v)} data-testid="planner-images-toggle"
            className={`px-2.5 py-1.5 rounded-full text-[10px] font-bold border flex items-center gap-1.5 ${showImages
              ? 'bg-purple-500/20 text-purple-200 border-purple-500/40'
              : 'bg-white/5 text-neutral-400 border-white/10 hover:text-white'}`}>
            <Sparkle size={11} weight={showImages ? 'fill' : 'regular'} />
            {showImages ? 'Hide images' : 'Show images'}
          </button>
          {showImages && missingChipNames.length > 0 && !paintJob && (
            <button onClick={startPaintAll} data-testid="planner-paint-all-btn"
              className="px-2.5 py-1.5 rounded-full text-[10px] font-bold border bg-purple-500/15 text-purple-200 border-purple-500/30 hover:bg-purple-500/25 flex items-center gap-1.5">
              <Sparkle size={11} weight="fill" /> Paint all ({missingChipNames.length} missing)
            </button>
          )}
          <button onClick={() => setBoardMode(v => !v)} data-testid="menu-board-toggle"
            className={`px-3 py-2 rounded-2xl text-xs sm:text-sm font-semibold flex items-center gap-1.5 border ${boardMode ? 'bg-[#FF6B35] text-white border-[#FF6B35]' : 'bg-purple-500/10 text-purple-200 border-purple-500/40 hover:bg-purple-500/20'}`}>
            🎨 {boardMode ? 'Exit Board' : 'Menu Board'}
          </button>
          <button onClick={() => setShowImport(true)} data-testid="planner-import-btn"
            className="px-3 py-2 rounded-2xl text-xs sm:text-sm font-semibold flex items-center gap-1.5 border bg-amber-500/10 text-amber-200 border-amber-500/40 hover:bg-amber-500/20">
            <DownloadSimple size={14} weight="bold" className="rotate-180" /> Import
          </button>
          <button onClick={() => setShowExport(true)} data-testid="planner-export-btn"
            className="px-3 py-2 rounded-2xl text-xs sm:text-sm font-semibold flex items-center gap-1.5 border bg-emerald-500/10 text-emerald-200 border-emerald-500/40 hover:bg-emerald-500/20">
            <DownloadSimple size={14} weight="bold" /> Export
          </button>
          <button onClick={() => setShowShopping(true)} data-testid="planner-shopping-btn"
            className="px-3 py-2 rounded-2xl text-xs sm:text-sm font-semibold flex items-center gap-1.5 border bg-blue-500/10 text-blue-200 border-blue-500/40 hover:bg-blue-500/20">
            <ListChecks size={14} weight="bold" /> Shopping
          </button>
          <button onClick={() => boardMode ? window.dispatchEvent(new CustomEvent('board-add-day')) : setShowPlanForm(true)} data-testid="add-plan-btn"
            className="px-3 sm:px-4 py-2 rounded-2xl bg-[#FF6B35] text-white text-xs sm:text-sm font-semibold flex items-center gap-1.5 btn-glow flex-shrink-0">
            <Plus size={14} /> {boardMode ? 'Add Day' : 'Plan Meal'}
          </button>
        </div>
      </div>

      {paintJob && (
        <div className="rounded-xl p-3" data-testid="planner-paint-progress"
          style={{ background: 'rgba(168,85,247,0.10)', border: '1px solid rgba(168,85,247,0.30)' }}>
          <div className="flex items-center justify-between gap-2 mb-1.5">
            <p className="text-[11px] font-bold text-purple-200 flex items-center gap-1.5">
              <Sparkle size={12} weight="fill" className={paintJob.status === 'running' ? 'animate-pulse' : ''} />
              {paintJob.status === 'complete'
                ? `${paintJob.auto ? 'Auto-painted' : 'Painted'} ${paintJob.done}/${paintJob.total} images${paintJob.failed > 0 ? ` · ${paintJob.failed} failed${paintJob.error ? ` (${String(paintJob.error).slice(0, 80)})` : ''}` : ' · all done!'}`
                : paintJob.status === 'stalled'
                  ? `Painting stalled at ${paintJob.done}/${paintJob.total} — auto-recovery will kick in shortly`
                  : `${paintJob.auto ? 'Auto-painting' : 'Painting'} ${paintJob.done}/${paintJob.total} images…`}
            </p>
            <div className="flex items-center gap-2">
              {paintJob.status === 'running' && <PaintEta job={paintJob} />}
              {paintJob.status === 'complete' && (
                <button onClick={() => setPaintJob(null)} className="text-[10px] text-purple-300 hover:text-white">Dismiss</button>
              )}
              {paintJob.status === 'stalled' && (
                <button onClick={() => { setPaintJob(null); startPaintAll(); }} data-testid="paint-retry-btn"
                  className="px-2.5 py-1 rounded-full text-[10px] font-bold bg-amber-500/20 text-amber-200 border border-amber-500/40 hover:bg-amber-500/30">
                  Retry now
                </button>
              )}
            </div>
          </div>
          <div className="h-1.5 rounded-full overflow-hidden" style={{ background: 'rgba(255,255,255,0.06)' }}>
            <div className="h-full transition-all duration-500"
              style={{
                width: `${paintJob.total > 0 ? Math.round((paintJob.done + paintJob.failed) / paintJob.total * 100) : 100}%`,
                background: paintJob.status === 'complete' && paintJob.failed > 0 ? '#F59E0B' : '#A855F7',
              }} />
          </div>
        </div>
      )}

      {selected.size > 0 && (
        <div className="rounded-xl px-3 py-2 flex items-center justify-between gap-2 flex-wrap"
          style={{ background: 'rgba(168,85,247,0.10)', border: '1px solid rgba(168,85,247,0.30)' }}
          data-testid="planner-bulk-bar">
          <p className="text-[11px] font-bold text-purple-200">{selected.size} day(s) selected</p>
          <div className="flex flex-wrap gap-1.5">
            <button onClick={() => runBulk('clear')} data-testid="planner-bulk-clear-dishes"
              className="px-2.5 py-1 rounded-lg bg-amber-500/15 text-amber-300 border border-amber-500/30 text-[10px] font-bold hover:bg-amber-500/25">
              Clear dishes
            </button>
            <button onClick={() => runBulk('delete')} data-testid="planner-bulk-delete"
              className="px-2.5 py-1 rounded-lg bg-red-500/15 text-red-300 border border-red-500/30 text-[10px] font-bold hover:bg-red-500/25">
              Delete days
            </button>
            <button onClick={clearSelection} data-testid="planner-bulk-clear-selection"
              className="px-2.5 py-1 rounded-lg bg-white/5 text-neutral-300 border border-white/10 text-[10px] font-bold hover:bg-white/10">
              Clear
            </button>
          </div>
        </div>
      )}

      {boardMode ? (
        <MenuBoardView api={api} plans={upcomingPlans} onUpdate={onUpdate} />
      ) : (
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-3">
        {upcomingPlans.length > 0 ? upcomingPlans.map(p => (
          <PlanDayCard
            key={p.plan_id || p.date}
            plan={p}
            jainFlag={jainFlags[p.date]}
            selected={selected.has(p.plan_id)}
            onToggleSelect={() => toggleSelect(p.plan_id)}
            onNotifyCook={handleNotifyCook}
            onSlotChange={handleSlotChange}
            onRegenerateSlot={handleRegenerateSlot}
            onClearDay={handleClearDay}
            onDeleteDay={handleDeleteDay}
            onRateDish={(dish, slot) => setRatingModal({ plan_id: p.plan_id, date: p.date, meal_type: slot, dish_name: dish })}
            onRecipeOpen={(payload) => setRecipePanelDish(payload)}
            onSwapChip={(dish, idx, slot) => setSwapState({ plan_id: p.plan_id, slot, dish, index: idx })}
            onSetServings={async (planId, newServings) => {
              try {
                await api.patch(`/meals/plans/${planId}`, { servings: newServings });
                onUpdate && onUpdate();
              } catch (e) {
                window.alert(e?.response?.data?.detail || 'Failed to update servings.');
              }
            }}
            hasStaffMeals={!!prefs?.has_staff_meals}
            staffSharingMode={prefs?.staff_sharing_mode}
            api={api}
            onUpdate={onUpdate}
            showImages={showImages}
            dishImages={dishImages}
            dishKeyOf={defaultDishKeyOf}
            onGenerateImage={generateImageFor}
            imagesFetched={imagesFetched}
            inFlightKeys={inFlightKeys}
            paintJobKeys={paintJobKeys}
            cookProgress={cookProgress}
          />
        )) : (
          <div className="col-span-full text-center py-12 rounded-2xl" style={{ background: 'rgba(255,255,255,0.02)', border: '1px solid rgba(255,255,255,0.06)' }}>
            <CookingPot size={36} className="text-neutral-600 mx-auto mb-3" />
            <p className="text-sm text-neutral-500">No upcoming meal plans. Click &quot;Plan Meal&quot; or use AI Chef.</p>
          </div>
        )}
      </div>
      )}

      {!boardMode && pastPlans.length > 0 && (
        <div className="mt-4">
          <button
            onClick={() => setShowArchive(v => !v)}
            data-testid="planner-archive-toggle"
            className="w-full flex items-center justify-between gap-2 px-4 py-2.5 rounded-xl transition-colors hover:bg-white/5"
            style={{ background: 'rgba(255,255,255,0.02)', border: '1px solid rgba(255,255,255,0.06)' }}
          >
            <div className="flex items-center gap-2 min-w-0">
              <span className="text-xs">📁</span>
              <span className="text-xs font-bold text-neutral-300">
                Archive
              </span>
              <span className="text-[10px] text-neutral-500">
                {pastPlans.length} past plan{pastPlans.length !== 1 ? 's' : ''} · newest first
              </span>
            </div>
            <span className="text-[10px] text-neutral-400">
              {showArchive ? '▲ Hide' : '▼ Show'}
            </span>
          </button>

          {showArchive && (
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-3 mt-3" data-testid="planner-archive-grid">
              {pastPlans.map(p => (
                <PlanDayCard
                  key={`arch-${p.plan_id || p.date}`}
                  plan={p}
                  jainFlag={jainFlags[p.date]}
                  selected={selected.has(p.plan_id)}
                  onToggleSelect={() => toggleSelect(p.plan_id)}
                  onNotifyCook={handleNotifyCook}
                  onSlotChange={handleSlotChange}
                  onRegenerateSlot={handleRegenerateSlot}
                  onClearDay={handleClearDay}
                  onDeleteDay={handleDeleteDay}
                  onRateDish={(dish, slot) => setRatingModal({ plan_id: p.plan_id, date: p.date, meal_type: slot, dish_name: dish })}
                  onRecipeOpen={(payload) => setRecipePanelDish(payload)}
                  onSwapChip={(dish, idx, slot) => setSwapState({ plan_id: p.plan_id, slot, dish, index: idx })}
                  onSetServings={async (planId, newServings) => {
                    try {
                      await api.patch(`/meals/plans/${planId}`, { servings: newServings });
                      onUpdate && onUpdate();
                    } catch (e) {
                      window.alert(e?.response?.data?.detail || 'Failed to update servings.');
                    }
                  }}
                  hasStaffMeals={!!prefs?.has_staff_meals}
                  staffSharingMode={prefs?.staff_sharing_mode}
                  api={api}
                  onUpdate={onUpdate}
                  showImages={showImages}
                  dishImages={dishImages}
                  dishKeyOf={defaultDishKeyOf}
                  onGenerateImage={generateImageFor}
                  imagesFetched={imagesFetched}
                  inFlightKeys={inFlightKeys}
                  paintJobKeys={paintJobKeys}
                  cookProgress={cookProgress}
                />
              ))}
            </div>
          )}
        </div>
      )}

      {showPlanForm && (
        <Modal onClose={() => setShowPlanForm(false)}>
          <h3 className="text-lg font-bold mb-4">Plan Meal</h3>
          <form onSubmit={handleSavePlan} className="space-y-3">
            <InputField label="Date" type="date" value={planForm.date} onChange={v => setPlanForm(f => ({ ...f, date: v }))} testId="plan-date" />
            <InputField label="Breakfast" value={planForm.breakfast} onChange={v => setPlanForm(f => ({ ...f, breakfast: v }))} testId="plan-breakfast" placeholder="e.g., Poha, Methi Thepla, Masala Chai" />
            <InputField label="Lunch" value={planForm.lunch} onChange={v => setPlanForm(f => ({ ...f, lunch: v }))} testId="plan-lunch" placeholder="e.g., Dal, Sabzi, Roti, Rice" />
            <InputField label="Dinner" value={planForm.dinner} onChange={v => setPlanForm(f => ({ ...f, dinner: v }))} testId="plan-dinner" placeholder="e.g., Paneer Masala, Roti, Salad" />
            <InputField label="Tea / Evening snack" value={planForm.snacks} onChange={v => setPlanForm(f => ({ ...f, snacks: v }))} testId="plan-snacks" placeholder="e.g., Samosa, Fruit, Chai" />
            <InputField label="Notes" value={planForm.notes} onChange={v => setPlanForm(f => ({ ...f, notes: v }))} testId="plan-notes" />
            <button type="submit" data-testid="save-plan-btn" className="w-full py-3 rounded-xl bg-[#FF6B35] text-white text-sm font-bold">Save Plan</button>
          </form>
        </Modal>
      )}

      {ratingModal && (
        <RatingModal item={ratingModal} api={api} onClose={() => setRatingModal(null)} />
      )}

      {showExport && (
        <ExportMenuModal api={api} prefs={prefs} onClose={() => setShowExport(false)} />
      )}
      {showImport && (
        <ImportPlansModal api={api} plans={plans} onClose={() => setShowImport(false)} onUpdate={onUpdate} />
      )}

      {showShopping && (
        <ShoppingListModal api={api} prefs={prefs} onClose={() => setShowShopping(false)} />
      )}

      {recipePanelDish && (
        <DishDetailDrawer api={api}
          dishName={typeof recipePanelDish === 'string' ? recipePanelDish : recipePanelDish.name}
          shareContext={typeof recipePanelDish === 'string' ? null : recipePanelDish}
          onClose={() => setRecipePanelDish(null)} />
      )}

      {swapState && (
        <SwapChipPopover api={api} swap={swapState} onClose={() => setSwapState(null)} onSwapped={onUpdate} />
      )}
    </div>
    <DragOverlay>
      {dragChip ? (
        <div className="inline-flex items-center gap-1 px-2 py-1 rounded-full text-[11px] font-medium shadow-2xl"
          style={{ background: '#FF6B35', border: '1px solid rgba(255,255,255,0.40)', color: 'white' }}>
          <DotsSixVertical size={10} weight="bold" />
          {dragChip.value}
        </div>
      ) : null}
    </DragOverlay>
    </DndContext>
  );
}
